<template>
  <b-card
    v-if="showShortcut && isAddon && loadingAddons"
    class="position-relative hover-card card-container"
    style="margin: 4px"
  >
    <fetching-data-spinner
      :loading="loadingAddons"
      :loading-title="$t('integrations.loading-addons')"
      min-height="30px"
      enable-retry
    />
  </b-card>
  <b-card
    v-else-if="showShortcut"
    class="position-relative hover-card card-container"
    style="margin: 4px"
    :body-class="{'shortcut-card':true, 'upgrade-bg-color':needUpgrade}"
    @click="handleShortcut"
  >
    <div
      class="d-flex align-items-center"
    >
      <div v-if="icon">
        <img
          class="shortcut-icon-image"
          :src="icon"
          width="40"
          height="40"
          alt="Icon"
        >
      </div>
      <div class="content-without-icon">
        <div class="label title one-line">
          {{ title }}
        </div>
        <div
          v-if="needUpgrade"
          class="title d-flex justify-content-between"
        >
          <b-badge
            v-if="needUpgrade"
            variant="light-danger"
            class="upgrade-title"
          >
            <div class="upgrade">
              {{ $t('subscription.upgrade') }}
            </div>
            <feather-icon
              class="mobile-upgrade"
              icon="ArrowUpIcon"
              size="5"
            />
          </b-badge>
        </div>
        <div
          v-if="isAddon"
          class="title d-flex justify-content-between"
        >
          <b-badge
            v-if="addon && isPurchased"
            variant="light-success"
            class="upgrade-title"
          >
            <div class="purchased">
              {{ $t('payment.purchased') }}
            </div>
            <feather-icon
              class="mobile-purchased"
              icon="CheckCircleIcon"
              size="5"
            />
          </b-badge>
          <span
            v-if="isAddon && addon && !isPurchased"
            class="text-primary price upgrade"
          >{{ addonPrice() + ' / ' + addonCycle() }}</span>
        </div>
      </div>

      <ok-tooltip
        v-if="tooltip"
        :text="tooltip"
      />
    </div></b-card>
</template>

<script>
import { BCard, BBadge } from 'bootstrap-vue'
import { mapGetters } from 'vuex'
import OkTooltip from '@/@okmenu/components/utils/OkTooltip'
import AddonCycleEnum from '@/models/subscription/AddonCycleEnum'
import { UPGRADED_SHORTCUTS } from '@/constants/shortcuts'
import { BUSINESS_FEATURES } from '@/constants/business-types'
import FetchingDataSpinner from '@/@okmenu/components/utils/FetchingDataSpinner'

const { PACKAGE_PERMISSIONS } = require('@/constants/features')

export default {
  name: 'ShortcutCard',
  components: {
    OkTooltip,
    BCard,
    BBadge,
    FetchingDataSpinner,
  },
  props: {
    icons: {
      type: Object,
      default: () => {},
    },
    value: {
      type: String,
      default: '',
    },
    tooltip: {
      type: String,
      default: null,
    },
    upgrade: {
      type: Boolean,
      default: true,
    },
    shortcut: {
      type: Object,
      default: () => {},
    },
    type: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      UPGRADED_SHORTCUTS,
      // loadingAddons: true,
    }
  },
  computed: {
    ...mapGetters({
      showShortcuts: 'navigation/showShortcuts',
      isGoogleViralPurchased: 'subscription/isGoogleViralPurchased',
      isBrandedDomainPurchased: 'subscription/isBrandedDomainPurchased',
      isMobileAppPurchased: 'subscription/isMobileAppPurchased',
      addons: 'pricing/addons',
      isAddonPurchased: 'subscription/isAddonPurchased',
      isProductService: 'businessTypes/isProductService',
    }),
    addon() {
      return this.filteredAddons().filter(addon => (addon.slug === this.shortcut.key))[0]
    },
    icon() {
      const iconKey = this.shortcut.key === 'mobile-app' ? 'mobile_app' : this.shortcut.key
      return this.needUpgrade ? this.icons[`${iconKey}_upgrade`] ?? this.icons[iconKey] : this.icons[iconKey]
    },
    isAddon() {
      return this.type === 'addons'
    },
    title() {
      return this.shortcut.key === 'dine_in' && this.isProductService ? this.$t('selling-types.types.product-service') : this.$t(this.shortcut.title)
    },
    needUpgrade() {
      switch (this.shortcut.key) {
        case 'queue':
          return !this.packageHasQueue
        case 'reservations':
          return !this.packageHasReservation
        case 'website_themes':
          return !this.packageHasQR
        case 'ipad_themes':
          return !this.packageHasIPAD
        default:
          break
      }
      if (this.shortcut.key === 'mobile_themes') return !this.isAddonPurchased('mobile-app')
      return false
    },
    showShortcut() {
      switch (this.shortcut.key) {
        case 'queue':
          return this.$store.getters['businessTypes/isEnabled'](BUSINESS_FEATURES.QUEUE) && !this.$store.getters['subscription/isQr']
        case 'pickup':
          return this.$store.getters['businessTypes/isEnabled'](BUSINESS_FEATURES.CAR_PICKUP)
        case 'reservations':
          return this.$store.getters['businessTypes/isEnabled'](BUSINESS_FEATURES.RESERVE_TABLE)
        case 'delivery':
          return this.$store.getters['businessTypes/isEnabled'](BUSINESS_FEATURES.DELIVERY)
        case 'dine_in':
          return this.$store.getters['businessTypes/isEnabled'](BUSINESS_FEATURES.DINE_IN)
        case 'mobile_themes':
          return this.isAddonPurchased('mobile-app')
        default:
          return true
      }
    },
    isPurchased() {
      return this.type === 'addons' ? this.isAddonPurchased(this.addon.slug) && !this.$store.getters['purchase/isAddonHasCount'](this.addon.slug) : false
    },
    canUpgrade() {
      return UPGRADED_SHORTCUTS.includes(this.shortcut.key)
    },
    packageHasQueue() {
      return this.$store.getters['subscription/canAccessFeature'](PACKAGE_PERMISSIONS.QUEUE)
    },
    packageHasReservation() {
      return this.$store.getters['subscription/canAccessFeature'](PACKAGE_PERMISSIONS.RESERVATION)
    },
    packageHasIPAD() {
      return this.$store.getters['subscription/canAccessFeature'](PACKAGE_PERMISSIONS.IPAD)
    },
    packageHasQR() {
      return this.$store.getters['subscription/canAccessFeature'](PACKAGE_PERMISSIONS.QR_CUSTOMIZATION)
    },
    loadingAddons() {
      return !this.addons.length
    },
  },
  mounted() {
  },
  methods: {
    handleShortcut() {
      let route = null
      if (this.isAddon) this.$router.push({ name: this.shortcut.route, params: { id: this.addon?.id } })
      else {
        if (this.canUpgrade && this.needUpgrade) route = this.shortcut.key === 'mobile_themes' ? this.addonRoute() : 'billing:plans'
        this.$router.push({ name: route ?? this.shortcut.route, query: { index: this.shortcut.index ?? 0, step: this.shortcut.index ?? 0 } })
      }
      this.$store.commit('navigation/SET_SHOW_SHORTCUTS', false)
    },
    filteredAddons() {
      return this.addons.filter(addon => !addon.integration_id && !addon.integration_categories?.length)
    },
    addonPrice() {
      switch (this.addon?.cycle) {
        case AddonCycleEnum.BOTH: return this.addon.monthly_price
        case AddonCycleEnum.YEARLY: return this.addon.yearly_price
        case AddonCycleEnum.MONTHLY: return this.addon.monthly_price
        case AddonCycleEnum.ONE_TIME: default: return this.addon?.price
      }
    },
    addonCycle() {
      switch (this.addon?.cycle) {
        case AddonCycleEnum.BOTH: return this.$t('payment.billed-monthly')
        case AddonCycleEnum.YEARLY: return this.$t('payment.billed-annually')
        case AddonCycleEnum.MONTHLY: return this.$t('payment.billed-monthly')
        case AddonCycleEnum.ONE_TIME: return this.$t('payment.billed-one-time')
        default: return this.$t('subscription.custom')
      }
    },
    addonRoute() {
      return { name: 'admin:addons:view', params: { id: this.addon?.id } }
    },
  },
}
</script>

<style lang="scss">
@import "@/assets/scss/utils/responsive.scss";

 .card-container{
   flex: 1 1 18% !important;
   @include respond-to-max('medium') {
     flex: 1 1 40% !important;
   }
 }
.shortcut-card {
  padding: 5px 12px !important;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  @include respond-to-max('medium') {
    padding: 5px 7px !important;
  }
  img {
    margin-right: 10px;
    [dir=rtl] & {
      margin-right: 0;
      margin-left: 10px;
    }
  }
  .title {
    font-size: 14px;
    font-weight: 600;
    line-height: 1.39;
    margin-bottom: 3px;
    padding: 2px;
    @include respond-to-max('medium') {
      font-size: 11px;
    }
  }
  .price {
    font-size: 12px;
    margin-top:5px;
  };
  .ok-tooltip-icon {
    color: var(--description-color);
    position: absolute;
    bottom: 13.4px;
    right: 13.4px;
  }
  .card-container {
    margin: 15px !important;
  }
}
.upgrade-bg-color {
  background: linear-gradient(0deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.8)), #4B465C;
}
.upgrade-title {
  font-size: 9px !important;
}
.hover-card:hover {
  background: #f2efeb !important;
  .dark-layout & {
    background: #FFFFFF;
    color: black;
  }
}
.shortcut-icon-image {
  width: 40px;
  height: 40px;
  @include respond-to-max('medium') {
    width: 25px;
    height: 25px;
  }
}
.mobile-upgrade{
  display: none;
  @include respond-to-max('medium') {
   display: block;
  }
}
.upgrade {
  display: block;
  @include respond-to-max('medium') {
    display: none;
  }
}
.mobile-purchased{
  display: none;
  @include respond-to-max('medium') {
    display: block;
  }
}
.purchased {
  display: block;
  @include respond-to-max('medium') {
    display: none;
  }
}
.content-without-icon {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  @include respond-to-max('medium') {
    flex-direction: row;
  }
}
</style>
